import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Card,
  CardContent,
  Grid,
  Avatar,
  useTheme,
  alpha,
  Fade,
  Slide,
  Skeleton,
  LinearProgress,
  Chip,
  Stack,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocationOn,
  Add,
  Refresh,
  Download,
  Search,
  FilterList,
  Clear,
  LocationCity,
  AttachMoney,
  TrendingUp
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSnackbar } from 'notistack';
import { districtService } from '../../services/districtService';
import { cityService } from '../../services/cityService';

const District = () => {
  const [districts, setDistricts] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    city_id: ''
  });

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadCities();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadDistricts(selectedCity);
    }
  }, [selectedCity]);

  const loadCities = async () => {
    try {
      const response = await cityService.getCities();
      setCities(response.data.filter(city => city.status === 'active'));
    } catch (error) {
      console.error('Error loading cities:', error);
      enqueueSnackbar('Ошибка загрузки городов', { variant: 'error' });
    }
  };

  const loadDistricts = async (cityId) => {
    try {
      setLoading(true);
      // districtService.getDistricts(cityId) returns the data array directly
      const data = await districtService.getDistricts(cityId);
      // Normalize to array in case backend wraps payload like { data: [...] }
      const items = Array.isArray(data) ? data : (data?.data || []);
      setDistricts(items);
      applyFilters(items, searchQuery, statusFilter, priceRange);
      enqueueSnackbar('Районы загружены', { variant: 'success' });
    } catch (error) {
      console.error('Error loading districts:', error);
      enqueueSnackbar('Ошибка загрузки районов', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Search and filtering
  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    applyFilters(districts, query, statusFilter, priceRange);
  };

  const handleStatusFilter = (event) => {
    const status = event.target.value;
    setStatusFilter(status);
    applyFilters(districts, searchQuery, status, priceRange);
  };

  const handlePriceRangeChange = (field, value) => {
    const newRange = { ...priceRange, [field]: value };
    setPriceRange(newRange);
    applyFilters(districts, searchQuery, statusFilter, newRange);
  };

  const applyFilters = (districtsData, query, status, range) => {
    let filtered = districtsData.filter(district => 
      district.name?.toLowerCase().includes(query)
    );

    if (status === 'active') {
      filtered = filtered.filter(district => district.status === 'active');
    } else if (status === 'inactive') {
      filtered = filtered.filter(district => district.status === 'inactive');
    }

    if (range.min !== '') {
      filtered = filtered.filter(district => parseFloat(district.price || 0) >= parseFloat(range.min));
    }
    if (range.max !== '') {
      filtered = filtered.filter(district => parseFloat(district.price || 0) <= parseFloat(range.max));
    }

    setFilteredDistricts(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriceRange({ min: '', max: '' });
    applyFilters(districts, '', 'all', { min: '', max: '' });
  };

  // Export functionality
  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();
      
      const selectedCityName = cities.find(c => c._id === selectedCity)?.name || 'Все города';
      
      const exportData = filteredDistricts.map((district, index) => ({
        '№': index + 1,
        'Название': district.name,
        'Город': cities.find(c => c._id === district.city_id)?.name || 'Не указан',
        'Цена': district.price ? `${district.price} ₽` : 'Не указана',
        'Статус': district.status === 'active' ? 'Активный' : 'Неактивный',
        'Дата создания': new Date(district.createdAt || Date.now()).toLocaleString('ru-RU'),
        'Порядок': district.order || index
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      const colWidths = [
        { wch: 5 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 10 }
      ];
      worksheet['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Районы');
      
      const fileName = `districts_${selectedCityName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      enqueueSnackbar('Данные экспортированы в Excel', { variant: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Ошибка экспорта данных', { variant: 'error' });
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(districts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setDistricts(items);
    try {
      const orderedDistricts = items.map((district, index) => ({
        id: district._id,
        order: index
      }));
      await districtService.updateOrder(orderedDistricts);
    } catch (error) {
      console.error('Error updating order:', error);
      loadDistricts(selectedCity);
    }
  };

  const handleAddDistrict = () => {
    setEditingDistrict(null);
    setFormData({
      name: '',
      city_id: selectedCity,
      price: 0
    });
    setOpenDialog(true);
  };

  const handleEditDistrict = (district) => {
    setEditingDistrict(district);
    setFormData({
      name: district.name,
      city_id: district.city_id || district.city?._id || selectedCity,
      price: district.price
    });
    setOpenDialog(true);
  };

  const handleDeleteDistrict = async (districtId) => {
    try {
      await districtService.deleteDistrict(districtId);
      await loadDistricts(selectedCity);
      enqueueSnackbar('Район удален', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting district:', error);
      enqueueSnackbar('Не удалось удалить район', { variant: 'error' });
    }
  };

  const handleToggleStatus = async (districtId) => {
    const district = districts.find(d => d._id === districtId);
    if (!district) {
      console.error('District not found');
      return;
    }
    const newStatus = district.status === 'active' ? 'inactive' : 'active';

    try {
      const { data: updatedDistrict } = await districtService.updateDistrict(districtId, { status: newStatus });
      setDistricts(districts.map(district =>
        district._id === districtId
          ? updatedDistrict
          : district
      ));
    } catch (error) {
      console.error('Error updating district status:', error);
    }
  };

  const handleSaveDistrict = async () => {
    try {
      // Ensure payload consistency and validation
      const name = (formData.name || '').trim();
      const cityId = formData.city_id || selectedCity;
      const priceVal = formData.price === '' || formData.price === null || typeof formData.price === 'undefined'
        ? undefined
        : Number(formData.price);

      if (!name) {
        enqueueSnackbar('Укажите название района', { variant: 'warning' });
        return;
      }
      if (!cityId) {
        enqueueSnackbar('Выберите город', { variant: 'warning' });
        return;
      }
      if (typeof priceVal !== 'undefined' && Number.isNaN(priceVal)) {
        enqueueSnackbar('Цена должна быть числом', { variant: 'warning' });
        return;
      }

      // Some backends expect city or city_id; send both for compatibility
      const payload = {
        name,
        city_id: cityId,
        city: cityId,
        ...(typeof priceVal !== 'undefined' ? { price: priceVal } : {})
      };

      if (editingDistrict) {
        await districtService.updateDistrict(editingDistrict._id, payload);
      } else {
        await districtService.createDistrict(payload);
      }
      // Reload from server to reflect any backend-calculated fields and ensure filters include the new item
      await loadDistricts(cityId);
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving district:', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Не удалось сохранить район';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {/* Hero Section */}
        <Paper 
          elevation={4}
          sx={{ 
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 3,
            mb: 3,
            color: 'text.primary'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <LocationOn sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Управление районами
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Настройка районов, цен доставки и порядка отображения
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Экспорт в Excel">
                <IconButton 
                  onClick={exportToExcel}
                  disabled={filteredDistricts.length === 0 || loading || !selectedCity}
                  sx={{ 
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              <Tooltip title="Обновить данные">
                <IconButton 
                  onClick={() => selectedCity && loadDistricts(selectedCity)}
                  disabled={loading || !selectedCity}
                  sx={{ 
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  {loading ? <LinearProgress size={20} sx={{ color: 'text.primary' }} /> : <Refresh />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Paper>

        {/* City Selection */}
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationCity color="primary" />
              Выбор города
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Выберите город для управления районами</InputLabel>
              <Select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                label="Выберите город для управления районами"
                sx={{
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  }
                }}
              >
                {cities.map((city) => (
                  <MenuItem key={city._id} value={city._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationCity fontSize="small" color="primary" />
                      {city.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {selectedCity && (
          <>
            {/* Search and Filters */}
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Search color="primary" />
                    Поиск и фильтры
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      size="small" 
                      onClick={clearFilters}
                      startIcon={<Clear />}
                      disabled={loading}
                    >
                      Сбросить
                    </Button>
                    <Button 
                      variant="contained"
                      onClick={handleAddDistrict}
                      startIcon={<Add />}
                      disabled={loading}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      Добавить район
                    </Button>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Поиск районов"
                      value={searchQuery}
                      onChange={handleSearch}
                      placeholder="Введите название района"
                      disabled={loading}
                      InputProps={{
                        startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Статус</InputLabel>
                      <Select
                        value={statusFilter}
                        onChange={handleStatusFilter}
                        label="Статус"
                        disabled={loading}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      >
                        <MenuItem value="all">Все статусы</MenuItem>
                        <MenuItem value="active">Только активные</MenuItem>
                        <MenuItem value="inactive">Только неактивные</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2.5}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Цена от"
                      type="number"
                      value={priceRange.min}
                      onChange={(e) => handlePriceRangeChange('min', e.target.value)}
                      placeholder="0"
                      disabled={loading}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₽</InputAdornment>
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2.5}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Цена до"
                      type="number"
                      value={priceRange.max}
                      onChange={(e) => handlePriceRangeChange('max', e.target.value)}
                      placeholder="∞"
                      disabled={loading}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₽</InputAdornment>
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Statistics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                        <Skeleton variant="text" width={100} height={24} />
                      </Box>
                      <Skeleton variant="text" width={80} height={48} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width={150} height={20} />
                    </Card>
                  </Grid>
                ))
              ) : (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      elevation={3}
                      sx={{ 
                        p: 3, 
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                          <LocationOn />
                        </Avatar>
                        <Typography variant="h6" color="primary" fontWeight="bold">
                          Всего районов
                        </Typography>
                      </Box>
                      <Typography variant="h3" fontWeight="bold" color="primary">
                        {districts.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        отфильтровано: {filteredDistricts.length}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      elevation={3}
                      sx={{ 
                        p: 3, 
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.15)}`
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                          <CheckCircleIcon />
                        </Avatar>
                        <Typography variant="h6" color="success.main" fontWeight="bold">
                          Активные
                        </Typography>
                      </Box>
                      <Typography variant="h3" fontWeight="bold" color="success.main">
                        {districts.filter(d => d.status === 'active').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        районов активны
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      elevation={3}
                      sx={{ 
                        p: 3, 
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 25px ${alpha(theme.palette.error.main, 0.15)}`
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ bgcolor: theme.palette.error.main, mr: 2 }}>
                          <CancelIcon />
                        </Avatar>
                        <Typography variant="h6" color="error.main" fontWeight="bold">
                          Неактивные
                        </Typography>
                      </Box>
                      <Typography variant="h3" fontWeight="bold" color="error.main">
                        {districts.filter(d => d.status === 'inactive').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        районов неактивны
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      elevation={3}
                      sx={{ 
                        p: 3, 
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 25px ${alpha(theme.palette.warning.main, 0.15)}`
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ bgcolor: theme.palette.warning.main, mr: 2 }}>
                          <AttachMoney />
                        </Avatar>
                        <Typography variant="h6" color="warning.main" fontWeight="bold">
                          Средняя цена
                        </Typography>
                      </Box>
                      <Typography variant="h3" fontWeight="bold" color="warning.main">
                        {districts.length > 0 
                          ? Math.round(districts.reduce((sum, d) => sum + (parseFloat(d.price) || 0), 0) / districts.length)
                          : 0
                        }₽
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        за доставку
                      </Typography>
                    </Card>
                  </Grid>
                </>
              )}
            </Grid>

            {/* Districts Table */}
            <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">
                  🏘️ Список районов ({loading ? '...' : filteredDistricts.length})
                </Typography>
                {!loading && filteredDistricts.length === 0 && districts.length > 0 && (
                  <Chip 
                    icon={<FilterList />} 
                    label="Нет результатов" 
                    color="warning" 
                    variant="outlined" 
                  />
                )}
              </Box>
              {loading && <LinearProgress />}
              
              {loading ? (
                <Box sx={{ p: 3 }}>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2 }}>
                      <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                      <Skeleton variant="text" width="25%" height={24} sx={{ mr: 2 }} />
                      <Skeleton variant="text" width="15%" height={24} sx={{ mr: 2 }} />
                      <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : filteredDistricts.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <LocationOn sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {districts.length === 0 ? 'Нет районов' : 'Нет результатов'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {districts.length === 0 
                      ? 'Добавьте первый район для выбранного города'
                      : 'Попробуйте изменить параметры поиска'
                    }
                  </Typography>
                  {districts.length === 0 && (
                    <Button 
                      variant="contained" 
                      startIcon={<Add />}
                      onClick={handleAddDistrict}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      Добавить первый район
                    </Button>
                  )}
                </Box>
              ) : (
                <TableContainer>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <TableCell width={50} sx={{ fontWeight: 'bold' }}>
                            <DragIndicatorIcon color="action" />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Район</TableCell>
                          <TableCell width={120} sx={{ fontWeight: 'bold' }}>Цена</TableCell>
                          <TableCell width={120} sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                          <TableCell width={150} align="right" sx={{ fontWeight: 'bold' }}>Действия</TableCell>
                        </TableRow>
                      </TableHead>
                      <Droppable droppableId="districts">
                        {(provided) => (
                          <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                            {filteredDistricts.map((district, index) => (
                              <Draggable key={district._id} draggableId={district._id} index={index}>
                                {(provided, snapshot) => (
                                  <TableRow
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    sx={{
                                      backgroundColor: snapshot.isDragging ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                        transform: 'translateX(4px)'
                                      }
                                    }}
                                  >
                                    <TableCell {...provided.dragHandleProps}>
                                      <DragIndicatorIcon 
                                        sx={{ 
                                          color: snapshot.isDragging ? theme.palette.primary.main : 'action.active',
                                          cursor: 'grab',
                                          '&:active': { cursor: 'grabbing' }
                                        }} 
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body1" fontWeight="medium">
                                        {district.name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        icon={<AttachMoney />}
                                        label={`${district.price || 0} ₽`}
                                        color="primary"
                                        size="small"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        icon={district.status === 'active' ? <CheckCircleIcon /> : <CancelIcon />}
                                        label={district.status === 'active' ? 'Активный' : 'Неактивный'}
                                        color={district.status === 'active' ? 'success' : 'error'}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleToggleStatus(district._id)}
                                        sx={{ 
                                          cursor: 'pointer',
                                          '&:hover': { 
                                            backgroundColor: district.status === 'active' 
                                              ? alpha(theme.palette.success.main, 0.1)
                                              : alpha(theme.palette.error.main, 0.1)
                                          }
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Tooltip title="Редактировать">
                                          <IconButton
                                            onClick={() => handleEditDistrict(district)}
                                            size="small"
                                            sx={{
                                              color: theme.palette.primary.main,
                                              '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                transform: 'scale(1.1)'
                                              }
                                            }}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Удалить">
                                          <IconButton
                                            onClick={() => handleDeleteDistrict(district._id)}
                                            size="small"
                                            sx={{
                                              color: theme.palette.error.main,
                                              '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                                transform: 'scale(1.1)'
                                              }
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </TableBody>
                        )}
                      </Droppable>
                    </Table>
                  </DragDropContext>
                </TableContainer>
              )}
            </Paper>
          </>
        )}

        {/* Enhanced Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            bgcolor: 'background.paper',
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main' }}>
              <LocationOn />
            </Avatar>
            {editingDistrict ? 'Редактировать район' : 'Добавить новый район'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Название района"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Введите название района"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  }
                }
              }}
            />
            <TextField
              margin="dense"
              label="Цена доставки"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder="0"
              InputProps={{
                startAdornment: <InputAdornment position="start">₽</InputAdornment>
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  }
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Город</InputLabel>
              <Select
                value={formData.city_id}
                onChange={(e) => setFormData({ ...formData, city_id: e.target.value })}
                label="Город"
                sx={{
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  }
                }}
              >
                {cities.map((city) => (
                  <MenuItem key={city._id} value={city._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationCity fontSize="small" color="primary" />
                      {city.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => setOpenDialog(false)}
              sx={{ borderRadius: 2 }}
            >
              Отмена
            </Button>
            <Button 
              onClick={handleSaveDistrict} 
              variant="contained"
              disabled={!formData.name.trim() || !formData.city_id}
              sx={{
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': {
                  background: theme.palette.action.disabledBackground
                }
              }}
            >
              {editingDistrict ? 'Сохранить изменения' : 'Добавить район'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );
};

export default District;
